<template>
  <div 
    class="machine-card"
    :class="getStatusBorderClass()"
  >
    <!-- 卡片头部 -->
    <div class="card-header">
      <div class="machine-name">
        <span class="status-indicator" :class="getStatusClass()"></span>
        {{ machine.machineName }}
      </div>
      <div class="status-badge" :class="getStatusBadgeClass()">
        {{ getMachineStatusText() }}
      </div>
    </div>
    
    <!-- 卡片内容 -->
    <div class="card-content">
      <!-- 核心指标区 -->
      <div class="core-metrics">
        <div class="metric-card level">
          <div class="metric-icon">
            <i class="el-icon-s-operation"></i>
          </div>
          <div class="metric-info">
            <div class="metric-label">关联级别</div>
            <div class="metric-value">{{ getAssociationLevelText() }}</div>
          </div>
        </div>
        
        <div class="metric-card quantity">
          <div class="metric-icon">
            <i class="el-icon-box"></i>
          </div>
          <div class="metric-info">
            <div class="metric-label">采集数量</div>
            <div class="metric-value">{{ formatNumber(getCollectedQuantity()) }}</div>
          </div>
        </div>
        
        <div class="metric-card rate" :class="getRateColorClass()">
          <div class="metric-icon">
            <i class="el-icon-data-analysis"></i>
          </div>
          <div class="metric-info">
            <div class="metric-label">关联率</div>
            <div class="metric-value">{{ machine.associationRate }}%</div>
          </div>
        </div>
        
        <div class="metric-card alarm" :class="{ 'has-alarm': machine.alarmCount > 0 }">
          <div class="metric-icon">
            <i class="el-icon-bell"></i>
          </div>
          <div class="metric-info">
            <div class="metric-label">报警次数</div>
            <div class="metric-value">{{ machine.alarmCount }}</div>
          </div>
        </div>
      </div>
      
      <!-- 相机信息 -->
      <div v-if="machine.cameras && machine.cameras.length > 0" class="camera-section">
        <div class="section-title">
          <i class="el-icon-video-camera"></i>
          相机监控
        </div>
        <div class="camera-grid">
          <div 
            v-for="camera in machine.cameras" 
            :key="camera.cameraName"
            class="camera-card"
          >
            <!-- 相机头部 -->
            <div class="camera-header">
              <div class="camera-name-wrapper">
                <i 
                  class="el-icon-video-camera camera-icon" 
                  :class="getCameraStatusClass(camera.cameraStatus)"
                ></i>
                <span class="camera-name">{{ camera.cameraName }}</span>
              </div>
              <div 
                class="camera-status-badge" 
                :class="getCameraStatusClass(camera.cameraStatus)"
              >
                {{ getCameraStatusText(camera.cameraStatus) }}
              </div>
            </div>
            
            <!-- 相机数据可视化 -->
            <div class="camera-stats">
              <!-- 采集率环形图 -->
              <div class="stat-circle">
                <svg viewBox="0 0 100 100" class="circle-svg">
                  <circle 
                    cx="50" 
                    cy="50" 
                    r="40" 
                    class="circle-bg"
                  />
                  <circle 
                    cx="50" 
                    cy="50" 
                    r="40" 
                    class="circle-progress"
                    :class="getRateCircleClass(camera.collectRate)"
                    :style="getCircleStyle(camera.collectRate)"
                  />
                </svg>
                <div class="circle-text">
                  <div class="rate-value">{{ camera.collectRate }}%</div>
                  <div class="rate-label">采集率</div>
                </div>
              </div>
              
              <!-- 数据指标 -->
              <div class="stat-metrics">
                <div class="metric-item">
                  <i class="el-icon-success metric-icon success"></i>
                  <div class="metric-content">
                    <div class="metric-value">{{ formatNumber(camera.collectCount) }}</div>
                    <div class="metric-label">采集数</div>
                  </div>
                </div>
                <div class="metric-item">
                  <i class="el-icon-error metric-icon error"></i>
                  <div class="metric-content">
                    <div class="metric-value">{{ camera.failCount }}</div>
                    <div class="metric-label">失败数</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MachineCard',
  props: {
    machine: {
      type: Object,
      required: true
    }
  },
  methods: {
    getStatusClass() {
      // 状态编码映射：1=运行, 2=停止, 3=未启动
      const statusMap = {
        '1': 'status-running',
        '2': 'status-stopped',
        '3': 'status-idle'
      }
      return statusMap[this.machine.machineStatus] || 'status-idle'
    },
    
    getStatusBorderClass() {
      // 根据工控机状态决定边框颜色
      const statusMap = {
        '1': 'border-green',    // 运行中 - 绿色
        '2': 'border-red',      // 已停止 - 红色
        '3': 'border-gray'      // 未启动 - 灰色
      }
      return statusMap[this.machine.machineStatus] || 'border-gray'
    },
    
    getBorderColorClass() {
      // 根据关联率决定边框颜色（保留此方法以备后用）
      const rate = this.machine.associationRate || 0
      if (rate >= 90) return 'border-green'
      if (rate >= 80) return 'border-yellow'
      return 'border-red'
    },
    
    getRateColorClass() {
      const rate = this.machine.associationRate || 0
      if (rate >= 90) return 'rate-green'
      if (rate >= 80) return 'rate-yellow'
      return 'rate-red'
    },
    
    getCameraStatusClass(status) {
      // 相机状态编码映射：1=在线, 2=离线
      const statusMap = {
        '1': 'camera-running',
        '2': 'camera-stopped'
      }
      return statusMap[status] || 'camera-idle'
    },
    
    getCameraStatusText(status) {
      // 状态编码转文字显示
      const statusMap = {
        '1': '在线',
        '2': '离线'
      }
      return statusMap[status] || '未知'
    },
    
    getMachineStatusText() {
      // 工控机状态编码转文字显示
      const statusMap = {
        '1': '运行中',
        '2': '已停止',
        '3': '未启动'
      }
      return statusMap[this.machine.machineStatus] || '未知'
    },
    
    getStatusBadgeClass() {
      // 状态徽章样式
      const statusMap = {
        '1': 'status-badge-running',
        '2': 'status-badge-stopped',
        '3': 'status-badge-idle'
      }
      return statusMap[this.machine.machineStatus] || 'status-badge-idle'
    },
    
    formatNumber(num) {
      if (!num && num !== 0) return '0'
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    
    getRateCircleClass(rate) {
      if (rate >= 90) return 'circle-excellent'  // 绿色：≥90%
      if (rate >= 80) return 'circle-warning'    // 黄色：80-89%
      return 'circle-danger'                      // 红色：<80%
    },
    
    getCircleStyle(rate) {
      const circumference = 2 * Math.PI * 40
      const offset = circumference - (rate / 100) * circumference
      return {
        strokeDasharray: `${circumference} ${circumference}`,
        strokeDashoffset: offset
      }
    },
    
    getAssociationLevelText() {
      // 优先使用后端返回的关联级别名称（如"盒箱关联"）
      if (this.machine.associationLevelName) {
        return this.machine.associationLevelName
      }
      // 如果没有名称，使用原来的逻辑作为后备
      return this.machine.associationLevel === 1 ? '父级' : '子级'
    },
    
    getCollectedQuantity() {
      // 根据关联级别返回对应的采集数量
      return this.machine.associationLevel === 1 
        ? this.machine.parentCollectCount 
        : this.machine.childCollectCount
    }
  }
}
</script>

<style lang="scss" scoped>
.machine-card {
  background: rgba(10, 20, 40, 0.7);
  border: 2px solid;
  border-radius: 10px;
  padding: 16px;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  transition: all 0.3s ease;
  clip-path: polygon(
    0 12px, 12px 0, 
    calc(100% - 12px) 0, 100% 12px,
    100% calc(100% - 12px), calc(100% - 12px) 100%,
    12px 100%, 0 calc(100% - 12px)
  );
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 8px 30px rgba(0, 255, 255, 0.4);
  }
  
  &.border-green {
    border-color: rgba(0, 255, 0, 0.7);
    box-shadow: 0 0 20px rgba(0, 255, 0, 0.3);
  }
  
  &.border-yellow {
    border-color: rgba(255, 255, 0, 0.7);
    box-shadow: 0 0 20px rgba(255, 255, 0, 0.3);
  }
  
  &.border-red {
    border-color: rgba(255, 0, 0, 0.7);
    box-shadow: 0 0 20px rgba(255, 0, 0, 0.3);
  }
  
  &.border-gray {
    border-color: rgba(128, 128, 128, 0.7);
    box-shadow: 0 0 20px rgba(128, 128, 128, 0.3);
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 12px;
  border-bottom: 2px solid rgba(0, 255, 255, 0.3);
}

.machine-name {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #00ffff;
  font-size: 20px;
  font-weight: bold;
  text-shadow: 0 0 15px rgba(0, 255, 255, 0.6);
  letter-spacing: 1px;
}

.status-indicator {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  
  &.status-running {
    background: #00ff00;
    box-shadow: 0 0 15px rgba(0, 255, 0, 1);
    animation: pulse 2s ease-in-out infinite;
  }
  
  &.status-stopped {
    background: #ff0000;
    box-shadow: 0 0 15px rgba(255, 0, 0, 1);
    animation: blink 1s step-start infinite;
  }
  
  &.status-idle {
    background: #666;
    box-shadow: 0 0 8px rgba(102, 102, 102, 0.5);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.2);
  }
}

@keyframes blink {
  0%, 50% {
    opacity: 1;
  }
  51%, 100% {
    opacity: 0.3;
  }
}

.alarm-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 0, 0, 0.2);
  border: 1px solid rgba(255, 0, 0, 0.5);
  border-radius: 12px;
  padding: 4px 10px;
  color: #ff4444;
  font-size: 14px;
  font-weight: bold;
  
  i {
    font-size: 16px;
  }
}

.status-badge {
  display: flex;
  align-items: center;
  border-radius: 12px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: bold;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  
  &.status-badge-running {
    background: rgba(0, 255, 0, 0.15);
    border: 1px solid rgba(0, 255, 0, 0.5);
    color: #00ff00;
    box-shadow: 0 0 10px rgba(0, 255, 0, 0.3);
    animation: status-pulse 2s ease-in-out infinite;
  }
  
  &.status-badge-stopped {
    background: rgba(255, 0, 0, 0.15);
    border: 1px solid rgba(255, 0, 0, 0.5);
    color: #ff4444;
    box-shadow: 0 0 10px rgba(255, 0, 0, 0.3);
  }
  
  &.status-badge-idle {
    background: rgba(128, 128, 128, 0.15);
    border: 1px solid rgba(128, 128, 128, 0.5);
    color: #999;
    box-shadow: 0 0 10px rgba(128, 128, 128, 0.2);
  }
}

@keyframes status-pulse {
  0%, 100% {
    box-shadow: 0 0 10px rgba(0, 255, 0, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(0, 255, 0, 0.6);
  }
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  overflow: hidden;
}

.core-metrics {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.metric-card {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(0, 255, 255, 0.2);
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.6);
    border-color: rgba(0, 255, 255, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 255, 255, 0.2);
  }
  
  &.level {
    border-left: 3px solid #00ffff;
  }
  
  &.quantity {
    border-left: 3px solid #00ccff;
  }
  
  &.rate {
    &.rate-green {
      border-left: 3px solid #00ff00;
      
      .metric-value {
        color: #00ff00;
        text-shadow: 0 0 10px rgba(0, 255, 0, 0.6);
      }
    }
    
    &.rate-yellow {
      border-left: 3px solid #ffff00;
      
      .metric-value {
        color: #ffff00;
        text-shadow: 0 0 10px rgba(255, 255, 0, 0.6);
      }
    }
    
    &.rate-red {
      border-left: 3px solid #ff4444;
      
      .metric-value {
        color: #ff4444;
        text-shadow: 0 0 10px rgba(255, 68, 68, 0.6);
      }
    }
  }
  
  &.alarm {
    border-left: 3px solid rgba(255, 255, 255, 0.3);
    
    &.has-alarm {
      border-left-color: #ff4444;
      background: rgba(255, 0, 0, 0.1);
      
      .metric-icon {
        color: #ff4444;
        animation: alarm-pulse 1.5s ease-in-out infinite;
      }
      
      .metric-value {
        color: #ff4444;
        text-shadow: 0 0 10px rgba(255, 68, 68, 0.6);
      }
    }
  }
}

@keyframes alarm-pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.7;
  }
}

.metric-icon {
  font-size: 28px;
  color: #00ffff;
  flex-shrink: 0;
  filter: drop-shadow(0 0 6px rgba(0, 255, 255, 0.4));
  
  i {
    display: block;
  }
}

.metric-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.metric-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1;
}

.metric-value {
  font-size: 20px;
  font-weight: bold;
  color: #00ffff;
  text-shadow: 0 0 8px rgba(0, 255, 255, 0.4);
  line-height: 1;
}

.camera-section {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 2px solid rgba(0, 255, 255, 0.3);
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #00ffff;
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
  flex-shrink: 0;
  
  i {
    font-size: 18px;
  }
}

.camera-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  flex: 1;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 255, 255, 0.3);
    border-radius: 2px;
    
    &:hover {
      background: rgba(0, 255, 255, 0.5);
    }
  }
}

.camera-card {
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(0, 255, 255, 0.2);
  border-radius: 8px;
  padding: 10px;
  transition: all 0.3s ease;
  height: fit-content;
  
  &:hover {
    border-color: rgba(0, 255, 255, 0.5);
    box-shadow: 0 0 15px rgba(0, 255, 255, 0.2);
    transform: translateY(-2px);
  }
}

.camera-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.camera-name-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
}

.camera-icon {
  font-size: 16px;
  
  &.camera-running {
    color: #00ff00;
    filter: drop-shadow(0 0 4px rgba(0, 255, 0, 0.8));
  }
  
  &.camera-stopped {
    color: #ff0000;
    filter: drop-shadow(0 0 4px rgba(255, 0, 0, 0.8));
  }
  
  &.camera-idle {
    color: #666;
  }
}

.camera-name {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 500;
}

.camera-status-badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: bold;
  
  &.camera-running {
    background: rgba(0, 255, 0, 0.2);
    color: #00ff00;
    border: 1px solid rgba(0, 255, 0, 0.5);
  }
  
  &.camera-stopped {
    background: rgba(255, 0, 0, 0.2);
    color: #ff0000;
    border: 1px solid rgba(255, 0, 0, 0.5);
  }
  
  &.camera-idle {
    background: rgba(102, 102, 102, 0.2);
    color: #999;
    border: 1px solid rgba(102, 102, 102, 0.5);
  }
}

.camera-stats {
  display: flex;
  gap: 12px;
  align-items: center;
}

.stat-circle {
  position: relative;
  width: 80px;
  height: 80px;
  flex-shrink: 0;
}

.circle-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.circle-bg {
  fill: none;
  stroke: rgba(255, 255, 255, 0.1);
  stroke-width: 6;
}

.circle-progress {
  fill: none;
  stroke-width: 6;
  stroke-linecap: round;
  transition: stroke-dashoffset 1s ease;
  
  &.circle-excellent {
    stroke: #00ff00;
    filter: drop-shadow(0 0 4px rgba(0, 255, 0, 0.6));
  }
  
  &.circle-warning {
    stroke: #ffff00;
    filter: drop-shadow(0 0 4px rgba(255, 255, 0, 0.6));
  }
  
  &.circle-danger {
    stroke: #ff0000;
    filter: drop-shadow(0 0 4px rgba(255, 0, 0, 0.6));
  }
}

.circle-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.rate-value {
  font-size: 18px;
  font-weight: bold;
  color: #00ffff;
  text-shadow: 0 0 8px rgba(0, 255, 255, 0.6);
  line-height: 1;
}

.rate-label {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 2px;
}

.stat-metrics {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.metric-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 6px;
  border-left: 3px solid transparent;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.5);
  }
}

.metric-icon {
  font-size: 20px;
  flex-shrink: 0;
  
  &.success {
    color: #00ff00;
    filter: drop-shadow(0 0 4px rgba(0, 255, 0, 0.6));
  }
  
  &.error {
    color: #ff4444;
    filter: drop-shadow(0 0 4px rgba(255, 68, 68, 0.6));
  }
}

.metric-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.metric-value {
  font-size: 16px;
  font-weight: bold;
  color: #ffffff;
  line-height: 1;
}

.metric-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
}
</style>
